import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Upload, Download, Loader2, CheckCircle, Clock, AlertTriangle, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { downloadMigrationTemplate, importLegacyConsents, getMigrationHistory, getReconsentStatus } from '@/services/dpdpa';

export default function LegacyMigrationPage() {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [graceDays, setGraceDays] = useState(90);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, h] = await Promise.all([getReconsentStatus(), getMigrationHistory()]);
      setStatus(s);
      setHistory(h.migrations || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadMigrationTemplate();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url; a.download = 'consent_migration_template.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Download failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const result = await importLegacyConsents(file, graceDays);
      toast.success(`Imported ${result.imported} consents (${result.skipped} skipped)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setUploading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  const progressColor = (status?.progress_pct || 0) >= 100 ? 'text-emerald-500' : (status?.progress_pct || 0) >= 50 ? 'text-amber-500' : 'text-red-500';

  return (
    <div className="p-6 space-y-6" data-testid="legacy-migration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Legacy Consent Migration</h1>
          <p className="text-sm opacity-60">Import historical consent records and manage DPDPA re-consent workflows</p>
        </div>
        <Button onClick={load} variant="ghost" size="icon"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {/* Re-consent Progress */}
      <Card data-testid="reconsent-progress-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Re-consent Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="text-center p-3 rounded-lg bg-blue-500/10">
              <p className="text-2xl font-bold text-blue-600">{status?.total_legacy || 0}</p>
              <p className="text-xs opacity-60">Total Legacy</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-amber-500/10">
              <p className="text-2xl font-bold text-amber-600">{status?.needs_reconsent || 0}</p>
              <p className="text-xs opacity-60">Needs Re-consent</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-red-500/10">
              <p className="text-2xl font-bold text-red-600">{status?.overdue || 0}</p>
              <p className="text-xs opacity-60">Overdue</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-emerald-500/10">
              <p className="text-2xl font-bold text-emerald-600">{status?.refreshed || 0}</p>
              <p className="text-xs opacity-60">Refreshed</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Progress value={status?.progress_pct || 0} className="flex-1 h-3" />
            <span className={`text-sm font-bold ${progressColor}`}>{status?.progress_pct || 0}%</span>
          </div>
        </CardContent>
      </Card>

      {/* Import Section */}
      <Card data-testid="import-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" /> Import Legacy Consents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label>Grace Period (days for re-consent)</Label>
              <Input type="number" value={graceDays} onChange={e => setGraceDays(Number(e.target.value))} className="mt-1" data-testid="grace-days-input" />
            </div>
            <Button onClick={handleDownloadTemplate} variant="outline" data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download CSV Template
            </Button>
            <div>
              <Label htmlFor="csv-upload" className="cursor-pointer">
                <Button asChild disabled={uploading}>
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload CSV
                  </label>
                </Button>
              </Label>
              <input id="csv-upload" type="file" accept=".csv" className="hidden" onChange={handleUpload} data-testid="csv-upload-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Migration History */}
      <Card data-testid="migration-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" /> Import History</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length > 0 ? (
            <div className="space-y-2">
              {history.map(m => (
                <div key={m.id} className="flex items-center justify-between p-3 rounded-lg border text-sm">
                  <div>
                    <p className="font-medium">{m.filename}</p>
                    <p className="text-xs opacity-50">{new Date(m.created_at).toLocaleString()} &middot; by {m.imported_by}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="default">{m.imported} imported</Badge>
                    {m.skipped > 0 && <Badge variant="secondary">{m.skipped} skipped</Badge>}
                    <span className="text-xs opacity-50">{m.grace_period_days}d grace</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-center py-4 opacity-50">No imports yet. Download the CSV template and upload your legacy consent data.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
